import { useEffect, useState } from "react";
import { api, errMsg, itDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FloppyDisk, Gear } from "@phosphor-icons/react";

const SEGNI = ["C", "NC", ""];
export const MODULI = [
  { modello: "01", titolo: "CL-01-15-M-00-00-01 rev. 01" },
  { modello: "02", titolo: "CL-01-15-M-00-00-02 rev. 00" },
];

const ModuloForm = ({ modello, layout, aggiorna }) => {
  const id = (resto) => `cl-${modello}-${resto}`;

  const toggleGiorno = (i) => aggiorna((l) => {
    const attivo = !l.giorni[i].attivo;
    l.giorni[i].attivo = attivo;
    l.pagine.forEach((p) => {
      p.orari[i] = attivo ? p.orari[i] : { dalle: "", alle: "" };
      p.righe.forEach((r) => { r.celle[i] = attivo ? (r.celle[i] || "C") : ""; });
      (p.temperature || []).forEach((t) => { t.valori[i] = attivo ? t.valori[i] : ""; });
    });
  });

  const ciclaCella = (pi, ri, ci) => aggiorna((l) => {
    const cur = l.pagine[pi].righe[ri].celle[ci] || "";
    l.pagine[pi].righe[ri].celle[ci] = SEGNI[(SEGNI.indexOf(cur) + 1) % SEGNI.length];
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["anno", "Anno"], ["dal", "Dal"], ["al", "Al"]].map(([k, lbl]) => (
          <div key={k}>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{lbl}</Label>
            <Input value={layout.header[k]} className="h-9 rounded-sm mt-1"
              data-testid={id(`header-${k}`)}
              onChange={(e) => aggiorna((l) => { l.header[k] = e.target.value; })} />
          </div>
        ))}
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          Giorni della settimana (togli la spunta per lasciare la colonna vuota)
        </div>
        <div className="flex flex-wrap gap-3">
          {layout.giorni.map((g, i) => (
            <label key={g.date} className="flex items-center gap-2 text-sm rounded-sm border border-border px-2 py-1">
              <input type="checkbox" checked={g.attivo} onChange={() => toggleGiorno(i)}
                data-testid={id(`day-${i}`)} />
              <span className="font-semibold">{g.label}</span>
              <span className="text-muted-foreground text-xs">{itDate(g.date)}</span>
              {g.festivo && <span className="text-[10px] font-semibold text-amber-600">FESTIVO</span>}
            </label>
          ))}
        </div>
      </div>

      {layout.pagine.map((p, pi) => (
        <div key={p.key} className="rounded-sm border border-border" data-testid={id(`page-${p.key}`)}>
          <div className="bg-secondary px-3 py-2 text-sm font-head font-bold">{p.titolo}</div>
          <div className="p-3 space-y-3">
            <div className="overflow-x-auto scroll-thin">
              <table className="w-full text-xs bc-no-cards">
                <thead><tr>
                  <th className="px-2 py-1 text-left text-[10px] uppercase text-muted-foreground w-64">Voce</th>
                  {layout.giorni.map((g) => (
                    <th key={g.date} className="px-1 py-1 text-[10px] uppercase text-muted-foreground text-center">
                      {g.label.slice(0, 3)}
                    </th>
                  ))}
                </tr></thead>
                <tbody>
                  {[["dalle", "Ora controllo — dalle"], ["alle", "Ora controllo — alle"]].map(([campo, lbl]) => (
                    <tr key={campo} className="border-t border-border">
                      <td className="px-2 py-1 font-semibold">{lbl}</td>
                      {p.orari.map((o, i) => (
                        <td key={i} className="px-1 py-1">
                          <Input value={o[campo]} className="h-8 rounded-sm text-center tabular-nums px-1"
                            data-testid={id(`${p.key}-${campo}-${i}`)}
                            onChange={(e) => aggiorna((l) => { l.pagine[pi].orari[i][campo] = e.target.value; })} />
                        </td>
                      ))}
                    </tr>
                  ))}
                  {p.righe.map((r, ri) => (
                    <tr key={ri} className="border-t border-border">
                      <td className="px-2 py-1">{r.label}</td>
                      {r.celle.map((c, ci) => (
                        <td key={ci} className="px-1 py-1 text-center">
                          <Input value={c} data-testid={id(`${p.key}-cell-${ri}-${ci}`)}
                            title="Scrivi C, NC o qualsiasi altro testo"
                            className={`h-8 rounded-sm text-center text-xs font-bold uppercase px-1
                              ${c.toUpperCase() === "NC" ? "border-rose-500 bg-rose-500/15 text-rose-600"
                                : c.toUpperCase() === "C" ? "border-emerald-500 bg-emerald-500/10" : ""}`}
                            onChange={(e) => aggiorna((l) => {
                              l.pagine[pi].righe[ri].celle[ci] = e.target.value;
                            })}
                            onDoubleClick={() => ciclaCella(pi, ri, ci)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                  {(p.temperature || []).map((t, ti) => (
                    <tr key={`t${ti}`} className="border-t border-border bg-secondary/40">
                      <td className="px-2 py-1 font-semibold">{t.label} (°C)</td>
                      {t.valori.map((v, vi) => (
                        <td key={vi} className="px-1 py-1">
                          <Input value={v} className="h-8 rounded-sm text-center tabular-nums px-1"
                            data-testid={id(`${p.key}-temp-${ti}-${vi}`)}
                            onChange={(e) => aggiorna((l) => {
                              l.pagine[pi].temperature[ti].valori[vi] = e.target.value;
                            })} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Ogni campo è modificabile: scrivi C (conforme), NC (non conforme), lascia vuoto oppure
              inserisci il testo che ti serve. Doppio clic sulla cella per il ciclo rapido C → NC → vuota.
            </p>
          </div>
        </div>
      ))}

      {(layout.nc || []).map((n, ni) => (
        <div key={n.key} className="rounded-sm border border-amber-500/50" data-testid={id(`nc-${n.key}`)}>
          <div className="bg-amber-500/10 px-3 py-2 text-sm font-head font-bold">
            {n.titolo} — azioni correttive
          </div>
          <div className="p-3 overflow-x-auto scroll-thin">
            <table className="w-full text-xs bc-no-cards">
              <thead><tr>
                {n.campi.map((c) => (
                  <th key={c.key} className="px-1 py-1 text-left text-[10px] uppercase text-muted-foreground">
                    {c.label}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {n.righe.map((r, ri) => (
                  <tr key={ri} className="border-t border-border">
                    {n.campi.map((c) => (
                      <td key={c.key} className="px-1 py-1 align-top">
                        <textarea rows={2} value={r[c.key] || ""}
                          data-testid={id(`${n.key}-${c.key}-${ri}`)}
                          className="w-full min-w-[86px] rounded-sm border border-input bg-transparent px-2 py-1 text-xs"
                          onChange={(e) => aggiorna((l) => { l.nc[ni].righe[ri][c.key] = e.target.value; })} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground mt-2">
              Compila una riga per ogni non conformità aperta: quello che scrivi viene stampato nel
              registro non conformità del modulo.
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

/** Modifica la settimana completa: prima il modulo CL-...-01 e poi il CL-...-02. */
export const ChecklistEditor = ({ dal, al, onSaved, onClose }) => {
  const [layouts, setLayouts] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all(MODULI.map((m) =>
      api.get("/checklist-pulizie/layout", { params: { dal, al, modello: m.modello } })))
      .then((r) => setLayouts(r.map((x) => x.data.layout)))
      .catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [dal, al]); // eslint-disable-line react-hooks/exhaustive-deps

  const aggiornaModulo = (indice) => (fn) => setLayouts((prev) => prev.map((l, i) => {
    if (i !== indice) return l;
    const next = JSON.parse(JSON.stringify(l));
    fn(next);
    return next;
  }));

  const salva = async () => {
    setSaving(true);
    try {
      for (const layout of layouts) {
        await api.post("/checklist-pulizie/pdf", { layout, save: true }, { responseType: "blob" });
      }
      toast.success("Check list della settimana salvata: il PDF è aggiornato");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-6xl max-h-[92vh] overflow-y-auto"
        data-testid="checklist-editor">
        <DialogHeader>
          <DialogTitle className="font-head">
            Check list settimana {itDate(dal)} – {itDate(al)}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Entrambi i moduli in un unico documento: prima il CL-01-15-M-00-00-01 rev. 01 e poi il
            CL-01-15-M-00-00-02 rev. 00. Correggi quello che serve e salva per aggiornare il PDF da stampare.
          </DialogDescription>
        </DialogHeader>

        {!layouts ? (
          <div className="py-10 text-center text-muted-foreground">Caricamento della check list...</div>
        ) : (
          <div className="space-y-8">
            {layouts.map((layout, i) => (
              <div key={MODULI[i].modello} className="space-y-4" data-testid={`cl-modulo-${MODULI[i].modello}`}>
                <div className="border-b-2 border-primary/60 pb-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                    Modulo {i + 1} di {layouts.length}
                  </span>
                  <div className="font-head font-bold">{MODULI[i].titolo}</div>
                </div>
                <ModuloForm modello={MODULI[i].modello} layout={layout}
                  aggiorna={aggiornaModulo(i)} />
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-sm" data-testid="cl-editor-cancel"
            onClick={onClose}>Annulla</Button>
          <Button className="rounded-sm font-semibold" disabled={!layouts || saving}
            data-testid="cl-editor-save" onClick={salva}>
            {saving ? <Gear size={15} className="mr-1.5 animate-spin" /> : <FloppyDisk size={15} className="mr-1.5" />}
            {saving ? "SALVATAGGIO..." : "SALVA E AGGIORNA PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
